import { getLtik } from "./ltik";

const getJsonErrorMessage = (jsonString: string) => {
  try {
    const parsed = JSON.parse(jsonString);

    if (parsed && typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch (error) {
    // not json, return the original string
  }
  return jsonString;
};

// Returns true if the provided content type represents HTML
const isHtmlContentType = (contentType: string | null) => {
  if (!contentType) return false;
  return contentType.toLowerCase().includes("text/html");
};

// Detect Cloudflare/gateway style HTML error pages or generic HTML error bodies
const getFriendlyHttpErrorMessage = (res: Response, rawText: string) => {
  const status = res.status;
  const contentType = res.headers.get("content-type");
  const looksLikeHtml =
    isHtmlContentType(contentType) ||
    /<!doctype html|<html[\s>]/i.test(rawText);

  // Map common Cloudflare/gateway errors to digestible text
  if (looksLikeHtml) {
    if (status === 524) {
      return "The request took too long and timed out. Please try again.";
    }
    if (status === 502 || status === 504) {
      return "The service is temporarily unavailable. Please try again shortly.";
    }
    if (
      status === 520 ||
      status === 521 ||
      status === 522 ||
      status === 523 ||
      status === 525 ||
      status === 526
    ) {
      return "A temporary connection issue occurred. Please try again soon.";
    }

    // Generic HTML error page (unexpected HTML response)
    return "An unexpected error page was returned. Please try again.";
  }

  // Fallback to any JSON/plain-text message the server provided
  return getJsonErrorMessage(rawText);
};

const getApiPath = () => {
  return __SHARED_CONFIG__.TOOL_PREFIX
    ? `/${__SHARED_CONFIG__.TOOL_PREFIX}`
    : "";
};

const fetchLti = async (path: string, options: RequestInit = {}) => {
  const ltik = getLtik();
  const apiPath = getApiPath();

  const defaultHeaders = {
    Authorization: `Bearer ${ltik}`,
  };

  try {
    const res = await fetch(`${apiPath}${path}`, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    return res;
  } catch (error) {
    // Normalize network/connection failures into a concise message
    throw new Error(
      "Network error: unable to reach the service. Please try again shortly.",
    );
  }
};

export const getApiKey = async () => {
  const res = await fetchLti("/micd-api-key");

  if (!res.ok) {
    const errorText = await res.text();
    const errorMessage = getFriendlyHttpErrorMessage(res, errorText);

    throw new Error(`Failed to fetch API key: ${errorMessage}`);
  }

  return res.json();
};

export const getMicdData = async () => {
  const res = await fetchLti("/micd-data");

  if (!res.ok) {
    const errorText = await res.text();
    const errorMessage = getFriendlyHttpErrorMessage(res, errorText);

    throw new Error(`Failed to fetch selection data: ${errorMessage}`);
  }

  return res.json();
};

export const postDeepLink = async (body: Record<string, unknown>) => {
  const res = await fetchLti("/deeplink", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errorText = await res.text();
    const errorMessage = getFriendlyHttpErrorMessage(res, errorText);

    throw new Error(`Failed to post deep link: ${errorMessage}`);
  }

  return res.text();
};
